import { useState, useEffect } from 'react';

interface UseNumberInputOptions {
  initialValue?: number | string;
  min?: number;
  max?: number;
  step?: number;
  allowEmpty?: boolean;
  onValidChange?: (value: number) => void;
}

interface UseNumberInputReturn {
  value: string;
  setValue: (value: string) => void;
  isValid: boolean;
  error: string | null;
  onBlur: () => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const useNumberInput = ({
  initialValue = '',
  min,
  max,
  step,
  allowEmpty = true,
  onValidChange
}: UseNumberInputOptions): UseNumberInputReturn => {
  const [value, setValue] = useState<string>(initialValue.toString());
  const [error, setError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState<boolean>(true);

  // Validate the input value
  const validate = (val: string): { valid: boolean; error: string | null } => {
    // Empty value handling
    if (val === '' || val === '-') {
      if (allowEmpty) {
        return { valid: true, error: null };
      } else {
        return { valid: false, error: 'Value is required' };
      }
    }

    // Check if it's a valid number
    const numValue = parseFloat(val);
    if (isNaN(numValue)) {
      return { valid: false, error: 'Please enter a valid number' };
    }

    // Check min constraint
    if (min !== undefined && numValue < min) {
      return { valid: false, error: `Value must be at least ${min}` };
    }

    // Check max constraint
    if (max !== undefined && numValue > max) {
      return { valid: false, error: `Value must be at most ${max}` };
    }

    // Check step constraint
    if (step !== undefined && step > 0) {
      const remainder = (numValue - (min || 0)) % step;
      if (remainder !== 0) {
        return { valid: false, error: `Value must be in increments of ${step}` };
      }
    }

    return { valid: true, error: null };
  };

  // Handle input change
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    
    // Allow typing negative sign at the beginning
    if (newValue === '-' && (min === undefined || min < 0)) {
      setValue(newValue);
      setError(null);
      setIsValid(true);
      return;
    }
    
    // Allow empty value
    if (newValue === '') {
      setValue(newValue);
      if (!allowEmpty) {
        setError('Value is required');
        setIsValid(false);
      } else {
        setError(null);
        setIsValid(true);
      }
      return;
    }

    // Allow typing decimal point
    if (newValue.endsWith('.') && !newValue.slice(0, -1).includes('.')) {
      setValue(newValue);
      setError(null);
      setIsValid(true);
      return;
    }

    // Allow valid number input
    if (/^-?\d*\.?\d*$/.test(newValue)) {
      setValue(newValue);
      // Don't validate while typing - only show errors on blur
      setError(null);
      setIsValid(true);
    }
  };

  // Handle blur event - perform full validation
  const onBlur = () => {
    const validation = validate(value);
    setIsValid(validation.valid);
    setError(validation.error);

    if (validation.valid && value !== '' && value !== '-') {
      const numValue = parseFloat(value);
      // Format the value if needed (remove trailing zeros, etc.)
      setValue(numValue.toString());
      
      // Call the callback if provided
      if (onValidChange) {
        onValidChange(numValue);
      }
    }
  };

  // Update initial value if it changes
  useEffect(() => {
    if (initialValue !== undefined) {
      setValue(initialValue.toString());
    }
  }, [initialValue]);

  return {
    value,
    setValue,
    isValid,
    error,
    onBlur,
    onChange
  };
};